import axios from 'axios';
import { BUILD_HASH, BUILD_TIMESTAMP } from '../version';

/**
 * Tells the API which build this tab is running, and tells the app when the API
 * says that build is too old (`409 client_outdated`).
 *
 * `X-Client-Build` is the hash (for humans and logs); `X-Client-Build-Time` is
 * the deploy order the server actually compares, because hashes do not sort.
 */
export const CLIENT_BUILD_HEADER = 'X-Client-Build';
export const CLIENT_BUILD_TIME_HEADER = 'X-Client-Build-Time';
export const CLIENT_OUTDATED_CODE = 'client_outdated';

export const clientBuildHeaders = (): Record<string, string> => ({
  [CLIENT_BUILD_HEADER]: BUILD_HASH,
  [CLIENT_BUILD_TIME_HEADER]: String(BUILD_TIMESTAMP),
});

type Listener = () => void;
const listeners = new Set<Listener>();

/** The provider subscribes here; the interceptors live outside React. */
export const onClientOutdated = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const notifyClientOutdated = (): void => {
  listeners.forEach((listener) => listener());
};

export const isClientOutdatedBody = (body: unknown): boolean =>
  Boolean(body) && typeof body === 'object' && (body as { code?: unknown }).code === CLIENT_OUTDATED_CODE;

/** Only our own API: a custom header on a third-party URL would force a CORS preflight. */
const isOwnApiUrl = (url: string | undefined, base: string): boolean => {
  if (!url) return false;
  return url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`);
};

const requestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.pathname + input.search;
  return input.url;
};

/**
 * Plain `fetch` calls to our API (several pages and contexts make them directly)
 * get the same headers and the same 409 handling. Calls through `makeApiCall`
 * already carry the headers; setting them again is harmless.
 */
function patchFetch(getApiBase: () => string): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = requestUrl(input);
    if (!isOwnApiUrl(url, getApiBase())) return originalFetch(input, init);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    Object.entries(clientBuildHeaders()).forEach(([name, value]) => headers.set(name, value));
    const response = await originalFetch(input, { ...init, headers });
    if (response.status === 409) {
      try {
        if (isClientOutdatedBody(await response.clone().json())) notifyClientOutdated();
      } catch {
        // Not JSON: an ordinary 409.
      }
    }
    return response;
  };
}

let installed = false;

/**
 * Install once on the default axios instance, which every service shares.
 * `getApiBase` is passed in (not imported) so `apiConfig` can use this module's
 * headers for fetch without an import cycle.
 */
export function installClientBuildInterceptors(getApiBase: () => string): void {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    if (isOwnApiUrl(config.url, getApiBase())) {
      Object.entries(clientBuildHeaders()).forEach(([name, value]) => {
        config.headers.set(name, value);
      });
    }
    return config;
  });
  patchFetch(getApiBase);
  axios.interceptors.response.use(undefined, (error) => {
    if (error?.response?.status === 409 && isClientOutdatedBody(error.response.data)) {
      notifyClientOutdated();
    }
    return Promise.reject(error);
  });
}
